import React, { useState } from 'react'
import {
  ArrowPathIcon,
  CheckCircleIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline'

import { Badge, Button, Input, Label, Spinner } from '../../../../components/ui'
import { useToasts } from '../../../../stores/useToasts'
import { useCleanupOperatingSowTest, useRunOperatingSowTest } from '../../api'
import { defaultOperatingFixture, getErrorMessage } from '../../domain/formatters'
import type { OperatingSowTestFixture } from '../../types'

function JsonBlock({ value }: { value: unknown }) {
  return (
    <pre className="max-h-[360px] overflow-auto rounded-lg bg-gray-950 p-4 text-xs leading-5 text-gray-100">
      {JSON.stringify(value ?? {}, null, 2)}
    </pre>
  )
}

function OperatingStatusBadge({ passed }: { passed?: boolean }) {
  if (passed === true) {
    return <Badge variant="success" className="rounded-md">Passed</Badge>
  }
  if (passed === false) {
    return <Badge variant="danger" className="rounded-md">Failed</Badge>
  }
  return <Badge variant="secondary" className="rounded-md">Not run</Badge>
}

export default function OperatingTestPanel() {
  const pushToast = useToasts(state => state.push)
  const [fixture, setFixture] = useState<OperatingSowTestFixture>(() => defaultOperatingFixture())
  const runOperatingTest = useRunOperatingSowTest()
  const cleanupOperatingTest = useCleanupOperatingSowTest()
  const run = runOperatingTest.data
  const result = run?.result as { positions?: unknown; allocations?: unknown } | undefined
  const rawPositions = result?.positions
  const positions = Array.isArray(rawPositions) ? rawPositions : []
  const rawAllocations = result?.allocations
  const allocations = Array.isArray(rawAllocations) ? rawAllocations : []
  const roleCountFields: Array<[string, keyof Pick<OperatingSowTestFixture, 'num_lead_fde' | 'num_fde' | 'num_pm'>]> = [
    ['Lead FDE', 'num_lead_fde'],
    ['FDE', 'num_fde'],
    ['PM', 'num_pm'],
  ]

  const updateFixture = <K extends keyof OperatingSowTestFixture>(
    key: K,
    value: OperatingSowTestFixture[K],
  ) => {
    setFixture(current => ({ ...current, [key]: value }))
  }

  const runTest = () => {
    runOperatingTest.mutate(fixture, {
      onSuccess: result => {
        pushToast({
          kind: result.passed ? 'success' : 'warning',
          message: result.passed
            ? `Operating test passed for ${result.projectName}.`
            : `Operating test finished for ${result.projectName}.`,
        })
      },
      onError: error => pushToast({ kind: 'error', message: getErrorMessage(error) }),
    })
  }

  const cleanup = () => {
    if (!run?.projectId) return
    cleanupOperatingTest.mutate(run.projectId, {
      onSuccess: result => {
        pushToast({
          kind: 'success',
          message: `Cleaned up Operating test project ${result.projectName}.`,
        })
      },
      onError: error => pushToast({ kind: 'error', message: getErrorMessage(error) }),
    })
  }

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-5 dark:border-dark-border dark:bg-dark-surface">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold text-charcoal dark:text-gray-100">SOW → Operating (test)</h2>
            <Badge variant="warning" className="rounded-md">Dev only</Badge>
            <OperatingStatusBadge passed={run?.passed} />
          </div>
          <p className="mt-1 max-w-3xl text-sm text-gray-500 dark:text-gray-400">
            Creates a live ZZZ-TEST project, writes unassigned role positions and tentative allocations,
            then reads them back without touching the approved-SOW handoff stub.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="brand"
            onClick={runTest}
            disabled={runOperatingTest.isPending || cleanupOperatingTest.isPending}
          >
            {runOperatingTest.isPending ? <Spinner size="sm" /> : <ArrowPathIcon className="h-4 w-4" />}
            Run live test
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={cleanup}
            disabled={!run?.projectId || cleanupOperatingTest.isPending}
          >
            {cleanupOperatingTest.isPending ? <Spinner size="sm" /> : <CheckCircleIcon className="h-4 w-4" />}
            Cleanup
          </Button>
        </div>
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <Label htmlFor="operating-test-client">Client</Label>
          <Input
            id="operating-test-client"
            className="mt-1"
            value={fixture.client}
            onChange={event => updateFixture('client', event.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="operating-test-start">Start date</Label>
          <Input
            id="operating-test-start"
            type="date"
            className="mt-1"
            value={fixture.start_date}
            onChange={event => updateFixture('start_date', event.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="operating-test-end">End date</Label>
          <Input
            id="operating-test-end"
            type="date"
            className="mt-1"
            value={fixture.end_date}
            onChange={event => updateFixture('end_date', event.target.value)}
          />
        </div>
        <div className="grid grid-cols-3 gap-2">
          {roleCountFields.map(([label, key]) => (
            <div key={key}>
              <Label htmlFor={`operating-test-${key}`}>{label}</Label>
              <Input
                id={`operating-test-${key}`}
                type="number"
                min={0}
                className="mt-1"
                value={fixture[key]}
                onChange={event => updateFixture(key, Number(event.target.value || 0))}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(260px,360px)_minmax(0,1fr)]">
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-bg">
          <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100">Execution trace</h3>
          <div className="mt-3 space-y-2">
            {(run?.steps?.length ? run.steps : [
              { name: 'project', status: runOperatingTest.isPending ? 'active' : 'pending', description: 'Create ZZZ-TEST project' },
              { name: 'positions', status: 'pending', description: 'Write unassigned positions' },
              { name: 'allocations', status: 'pending', description: 'Write tentative allocations' },
              { name: 'read-back', status: 'pending', description: 'Verify Operating read-back' },
            ]).map(step => (
              <div key={step.name} className="flex items-start justify-between gap-3 rounded-md bg-white px-3 py-2 dark:bg-dark-surface">
                <div>
                  <p className="text-sm font-medium text-charcoal dark:text-gray-100">{step.name}</p>
                  {step.description && (
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{step.description}</p>
                  )}
                </div>
                <Badge
                  variant={step.status === 'completed' ? 'success' : step.status === 'active' ? 'info' : 'secondary'}
                  className="rounded-md"
                >
                  {step.status}
                </Badge>
              </div>
            ))}
          </div>
        </div>

        <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-bg">
          {run ? (
            <>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100">{run.projectName}</h3>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Operating project {run.projectId}</p>
                </div>
                <Badge variant="success" className="rounded-md">
                  {positions.length} positions · {allocations.length} allocations
                </Badge>
              </div>
              <JsonBlock value={run.result} />
              <p className="mt-3 break-all text-xs text-gray-500 dark:text-gray-400">
                Cleanup: {run.cleanupCommand}
              </p>
            </>
          ) : (
            <div className="flex min-h-[220px] items-center justify-center rounded-lg border border-dashed border-gray-300 text-center dark:border-dark-border">
              <div className="max-w-sm p-6">
                <Cog6ToothIcon className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-3 text-sm font-medium text-charcoal dark:text-gray-100">No Operating run yet</p>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Run the guarded ZZZ-TEST fixture to see the project, positions, allocations, and read-back payload.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

